import { TitleComponent } from '@/widgets/page-header';
import { SendFeedbackIcon, mesageIcon } from '@/shared/ui/assets';
import { CustomButton, CustomTabs } from '@/shared/ui/components';
import { auth } from '@/shared/lib';
import classNames from 'classnames';
import { useMemo } from 'react';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import SupportRequest from './supportRequest';
import HowToVideos from './howToVideos/howtoVideos';
import FrequentlyAskedQuestions from './frequentlyAskedQuestions/frequentlyAskedQuestions';
import DeviceManuals from './deviceManuals/deviceManuals';
import BillingReference from './billingReference/billingReference';
import SendFeedbackModal from './sendFeedbackModal/sendFeedbackModal';
import { formatHowToVideos } from '../lib/support';
import { useSupport } from './useSupport';
import './support.scss';
import SupportModules from './supportModules/supportModules';

/**
 * The support page shell, presenting support requests, FAQ, how-to videos, billing reference, support modules, device manuals and a contact panel as vertical tabs. Builds the memoized tab list from useSupport state, marks each tab active from state.activeTab, shows the unread ticket comment count capped at 99+, exposes the support email and phone in the disabled contact tab, and hosts the send-feedback modal.
 *
 * @returns The support page markup with its vertical tabs and feedback modal.
 */
const SupportPage = () => {
  const {
    supportFacilitiesRedux,
    selectedFacilityRedux,
    supportEmail,
    headerRef,
    state,
    navigateTab,
    toggleSendFeedbackModal,
  } = useSupport();

  /**
   * Memoized tab configuration for the support page: support request, FAQ, how-to videos, billing reference, support modules, device manuals, and a disabled contact panel. Every tab passes an isActive flag derived from the active key, the support-request tab receives the facility list and selection, video data is shaped by the how-to video formatter, and the contact panel exposes the support email, phone number and the send-feedback button.
   *
   * @returns Tab descriptors consumed by the page's vertical tab component.
   */
  const supportTabs = useMemo(
    () => [
      {
        key: '1',
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Support request`}</span>
            {state.ticketCommentCount > 0 && (
              <div className="comment-count">
                {state.ticketCommentCount >= 100 ? '99+' : state.ticketCommentCount}
                <img src={mesageIcon} alt="Comment icon" />
              </div>
            )}
          </div>
        ),
        children: (
          <SupportRequest
            isActive={state.activeTab === '1'}
            supportFacilities={supportFacilitiesRedux ?? []}
            selectedFacility={selectedFacilityRedux}
          />
        ),
      },
      {
        key: '2',
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`FAQ`}</span>
          </div>
        ),
        children: <FrequentlyAskedQuestions isActive={state.activeTab === '2'} />,
      },
      {
        key: '3',
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`How to videos`}</span>
          </div>
        ),
        children: (
          <HowToVideos
            isActive={state.activeTab === '3'}
            howtoVideos={formatHowToVideos(
              state.supportGuideline.howtoVideos,
              state.supportGuideline.howToVideosBiotres,
            )}
          />
        ),
      },
      {
        key: '4',
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Billing reference`}</span>
          </div>
        ),
        children: (
          <BillingReference
            isActive={state.activeTab === '4'}
            url={state.supportGuideline.billingReference}
          />
        ),
      },
      {
        key: '5',
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Support modules`}</span>
          </div>
        ),
        children: <SupportModules isActive={state.activeTab === '5'} />,
      },
      {
        key: '6',
        label: (
          <div className="tab-container">
            <span className="tab-text">{t`Device manuals`}</span>
          </div>
        ),
        children: (
          <DeviceManuals
            isActive={state.activeTab === '6'}
            materials={state?.supportGuideline?.materials}
          />
        ),
      },
      {
        label: (
          <div className="tab-info support-contact">
            <p className="contact">{t`Support contact`}</p>
            <div className="contact-flex">
              <span>{`${t`Email`}:`}</span>
              <a className="link-contact text-right" href={`mailto:${supportEmail}`}>
                {supportEmail}
              </a>
            </div>
            <div className="contact-flex">
              <span>{`${t`Phone`}:`}</span>
              <span className="text-right">1-800-590-4155</span>
            </div>
            <CustomButton
              style="default"
              type="secondary"
              size="small"
              id="send-feedback-btn"
              className="send-feedback-btn"
              icon={<ReactSVG src={SendFeedbackIcon} />}
              onClick={toggleSendFeedbackModal}
            >
              {t`Send feedback`}
            </CustomButton>
          </div>
        ),
        disabled: true,
      },
    ],
    [
      state.activeTab,
      state.supportGuideline,
      state.ticketCommentCount,
      supportFacilitiesRedux,
      selectedFacilityRedux,
      supportEmail,
    ],
  );

  return (
    <>
      <SendFeedbackModal
        open={state.isOpenSendFeedbackModal}
        toggleSendFeedbackModal={toggleSendFeedbackModal}
      />

      <div
        ref={headerRef}
        className={classNames(
          'custom-page custom-page-has-only-title support-page animated fadeIn support-page',
          {
            'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
          },
        )}
      >
        <div className="top-container">
          <TitleComponent title={t`Support`} isShowListFacility />
        </div>

        <CustomTabs
          mode="vertical"
          className="custom-style-tab support-custom-tab"
          activeKey={state.activeTab}
          onChange={(key) => {
            navigateTab(key);
          }}
          items={supportTabs as any}
        />
      </div>
    </>
  );
};

export default SupportPage;
